/**
 * Project Multi-Select Component
 *
 * Allows selecting multiple projects for dashboard filtering
 * - "All Projects" option (default)
 * - Individual project selection
 * - Shows selected count in trigger button
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { logError } from '@/lib/logger';
import { projectsListOptions } from '@/lib/query-options';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { FilterTrigger } from './FilterTrigger';
// SSOT for the filter cap — the backend's shared projectIdsFilterSchema
// (packages/api-types/src/schemas/common.ts) 400s past this, so the UI must
// enforce the SAME number, never a hardcoded guess that could drift from it.
import { PROJECT_IDS_MAX } from '@aica/api-types';

interface Project {
  id: string;
  name: string;
}

/** The projects the dashboard filter offers — active + paused only. */
function dashboardProjectsQueryOptions(
  apiClient: Parameters<typeof projectsListOptions>[0],
  orgId: Parameters<typeof projectsListOptions>[1]
) {
  return projectsListOptions(apiClient, orgId, {
    page: 1,
    limit: 100,
    // Dashboard filter must show only active + paused — archived projects
    // are a deliberate exclusion (owner decision 2026-07-17, BACKLOG.md),
    // matching the existing "Все" precedent in -projects.lazy.tsx.
    statuses: ['active', 'paused'],
  });
}

interface ProjectMultiSelectProps {
  selectedProjectIds: string[];
  onChange: (projectIds: string[]) => void;
  className?: string;
}

export function ProjectMultiSelect({
  selectedProjectIds,
  onChange,
  className,
}: ProjectMultiSelectProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { apiClient, orgId } = useAuth();
  const projectsQuery = useQuery(dashboardProjectsQueryOptions(apiClient, orgId));
  const projects = (projectsQuery.data?.projects ?? []) as Project[];
  const loading = projectsQuery.isLoading;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!projectsQuery.error) {
      return;
    }
    logError(
      'project_multi_select.fetch_projects_failed',
      { component: 'ProjectMultiSelect' },
      projectsQuery.error instanceof Error ? projectsQuery.error : undefined
    );
  }, [projectsQuery.error, projectsQuery.errorUpdatedAt]);

  const allSelected = selectedProjectIds.length === 0;

  const handleToggleAll = () => {
    if (allSelected) {
      // If all selected, do nothing (already showing all)
      return;
    }
    // Select all = empty array (show all projects)
    onChange([]);
  };

  // Cap enforcement (K7): the backend 400s past PROJECT_IDS_MAX, so adding a
  // (PROJECT_IDS_MAX + 1)-th project must never leave silently — the item is
  // rendered `disabled` below with a reason, and this is the defense-in-depth
  // guard for any path that bypasses the disabled state (e.g. a stale
  // pointerdown queued before re-render).
  const atCap = selectedProjectIds.length >= PROJECT_IDS_MAX;

  const handleToggleProject = (projectId: string) => {
    if (selectedProjectIds.includes(projectId)) {
      // Remove project
      const newSelection = selectedProjectIds.filter((id) => id !== projectId);
      onChange(newSelection);
    } else if (!atCap) {
      // Add project — refused once the cap is reached, never a silent trim.
      onChange([...selectedProjectIds, projectId]);
    }
  };

  const getTriggerLabel = () => {
    if (allSelected) {
      return t('allProjects');
    }
    if (selectedProjectIds.length === 1) {
      const project = projects.find((p) => p.id === selectedProjectIds[0]);
      return project?.name || t('oneProject');
    }
    return t('multipleProjects', { count: selectedProjectIds.length });
  };

  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuPrimitive.Trigger asChild>
        <FilterTrigger
          className={className}
          disabled={loading}
          icon={<FolderOpen className="h-4 w-4" />}
          label={loading ? t('common:common.loading') : getTriggerLabel()}
        />
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 min-w-[220px] max-h-[400px] overflow-y-auto',
            'rounded-lg border border-border bg-bg-elevated shadow-lg',
            'animate-in fade-in-80 slide-in-from-top-2'
          )}
        >
          {/* All Projects Option */}
          <div
            className={cn(
              'flex items-center gap-3 px-3 py-2 text-sm cursor-pointer',
              'hover:bg-bg-surface transition-colors',
              allSelected && 'bg-primary-dim'
            )}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleAll();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleToggleAll();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div
              className={cn(
                'flex h-4 w-4 items-center justify-center rounded border',
                allSelected ? 'bg-primary border-primary' : 'border-border bg-bg'
              )}
            >
              {allSelected && <Check className="h-3 w-3 text-bg-void" />}
            </div>
            <span className={cn('flex-1', allSelected && 'font-medium text-fg')}>
              {t('allProjects')}
            </span>
          </div>

          <DropdownMenuPrimitive.Separator className="h-px bg-border my-1" />

          {/* Cap notice — visible the moment the limit is hit, so an
              unselectable item never reads as a bug. */}
          {atCap && (
            <div
              className="px-3 py-1.5 text-2xs text-warning-fg bg-warning-dim"
              data-testid="project-multi-select-cap-notice"
            >
              {t('projectLimitReached', {
                max: PROJECT_IDS_MAX,
                defaultValue: `Maximum ${PROJECT_IDS_MAX} projects selected`,
              })}
            </div>
          )}

          {/* Individual Projects */}
          {projects.length === 0 ? (
            <div className="px-3 py-2 text-sm text-fg-muted">
              {loading ? t('common:common.loading') : t('noProjects')}
            </div>
          ) : (
            projects.map((project) => {
              const isSelected = selectedProjectIds.includes(project.id);
              // Once the cap is reached, unselected items are disabled rather
              // than silently unresponsive — the cap notice above explains
              // why, and this title gives the same reason on hover/focus for
              // the item itself.
              const isDisabled = !isSelected && atCap;
              return (
                <div
                  key={project.id}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                    isDisabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer hover:bg-bg-surface',
                    isSelected && 'bg-primary-dim'
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDisabled) return;
                    handleToggleProject(project.id);
                  }}
                  onKeyDown={(e) => {
                    if (isDisabled) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      handleToggleProject(project.id);
                    }
                  }}
                  role="button"
                  aria-disabled={isDisabled}
                  tabIndex={isDisabled ? -1 : 0}
                  title={
                    isDisabled
                      ? t('projectLimitReached', {
                          max: PROJECT_IDS_MAX,
                          defaultValue: `Maximum ${PROJECT_IDS_MAX} projects selected`,
                        })
                      : undefined
                  }
                >
                  <div
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded border',
                      isSelected ? 'bg-primary border-primary' : 'border-border bg-bg'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3 text-bg-void" />}
                  </div>
                  <span className={cn('flex-1', isSelected && 'font-medium text-fg')}>
                    {project.name}
                  </span>
                </div>
              );
            })
          )}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
